import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link2, Upload, PenLine, Brain, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/api";

const MODES = [
  { id: "url", label: "Paste link", icon: Link2 },
  { id: "image", label: "Upload image", icon: Upload },
  { id: "thought", label: "Quick thought", icon: PenLine },
];

const DESTS = [
  { id: "brain", label: "Brain archive", sub: "Something new I'm exploring", icon: Brain },
  { id: "archive", label: "Archive", sub: "Work I've completed", icon: Layers },
];

export default function CaptureModal({ open, onClose }) {
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState("url");
  const [dest, setDest] = useState("brain");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setMode("url");
    setDest("brain");
    setUrl("");
    setText("");
    setNote("");
    setFile(null);
  };

  const submit = async () => {
    setLoading(true);
    try {
      let entry;
      if (mode === "image") {
        if (!file) throw new Error("Choose an image first");
        const fd = new FormData();
        fd.append("file", file);
        fd.append("type", dest);
        fd.append("note", note);
        entry = await api.upload("/entries/upload", fd);
      } else {
        entry = await api.post("/entries", {
          type: dest,
          url: mode === "url" ? url : undefined,
          text: mode === "thought" ? text : undefined,
          note,
        });
      }
      qc.invalidateQueries();
      toast.success(dest === "brain" ? "Added to your Brain archive" : "Added to your Archive");
      onClose();
      reset();
      navigate(`/entry/${entry.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Capture failed");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    !loading && (mode === "url" ? url.trim().length > 3 : mode === "thought" ? text.trim().length > 0 : !!file);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="capture-modal"
        className="max-w-lg rounded-3xl max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Capture something</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 rounded-full bg-secondary p-1">
          {MODES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`capture-mode-${id}`}
              onClick={() => setMode(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold transition-[background-color,color] ${
                mode === id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-1 space-y-3">
          {mode === "url" && (
            <Input
              data-testid="capture-url-input"
              placeholder="Paste any link — article, video, paper…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          )}
          {mode === "thought" && (
            <Textarea
              data-testid="capture-thought-input"
              placeholder="What's on your mind? An idea, an observation…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              autoFocus
            />
          )}
          {mode === "image" && (
            <label className="block rounded-2xl border-2 border-dashed border-border bg-secondary/50 px-4 py-8 text-center cursor-pointer hover:border-primary/50 transition-[border-color]">
              <input
                data-testid="capture-file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <span className="text-sm font-medium text-foreground">
                {file ? file.name : "Tap to choose an image"}
              </span>
            </label>
          )}
          <Input
            data-testid="capture-note-input"
            placeholder="Add a note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {DESTS.map(({ id, label, sub, icon: Icon }) => (
            <button
              key={id}
              data-testid={`capture-dest-${id}`}
              onClick={() => setDest(id)}
              className={`rounded-2xl border p-3 text-left transition-[border-color,background-color] ${
                dest === id ? "border-primary bg-primary/5" : "border-border bg-card"
              }`}
            >
              <Icon className={`h-4 w-4 ${dest === id ? "text-primary" : "text-muted-foreground"}`} />
              <p className="mt-1.5 text-sm font-semibold">{label}</p>
              <p className="text-[11px] text-muted-foreground">{sub}</p>
            </button>
          ))}
        </div>

        <Button
          data-testid="capture-submit-button"
          onClick={submit}
          disabled={!canSubmit}
          className="w-full rounded-full py-6 font-semibold"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Reading, thinking, writing a summary…
            </>
          ) : (
            "Capture"
          )}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground -mt-2 pb-1">
          AI writes the summary and tags for you. Private by default.
        </p>
      </DialogContent>
    </Dialog>
  );
}
